/**
 * The activity feed.
 *
 * Deliberately sparse: it records the moments that change the shape of a
 * round - a meld laid down, a book completed, a Foot picked up, a player
 * going out - and leaves the draws and discards between them to the table
 * itself. Those are still announced to screen readers as they happen; see
 * the turn log's live region.
 *
 * Each round opens with a divider entry, so a game's worth of activity reads
 * as four chapters rather than one long run.
 */

import type { TableEvent } from "../server/protocol.js";

/** A line of play, or the divider that opens a round. */
export type LogKind = "play" | "round";

export interface LoggedEvent {
    readonly id: number;
    readonly kind: LogKind;
    readonly text: string;
    /** True when a bot did this, so the feed can mark it apart from your own play. */
    readonly automated: boolean;
}

/** Entries kept in memory; older ones scroll away rather than growing forever. */
export const LOG_LIMIT = 200;

export interface LogEntryInput {
    readonly kind?: LogKind;
    readonly text: string;
    readonly automated?: boolean;
}

export function appendEvent(
    events: readonly LoggedEvent[],
    entry: LogEntryInput,
    limit = LOG_LIMIT
): readonly LoggedEvent[] {
    const id = (events.at(-1)?.id ?? 0) + 1;

    const next: LoggedEvent = {
        id,
        kind: entry.kind ?? "play",
        text: entry.text,
        automated: entry.automated ?? false
    };

    return [...events, next].slice(-limit);
}

/**
 * The feed's line for a table event, or null when the event achieved nothing
 * the feed shows. One line per action, however many highlights it had: a meld
 * that is laid down already a book reads as one thing that happened.
 */
export function describeHighlights(event: TableEvent): string | null {
    if (event.highlights.length === 0) {
        return null;
    }

    return `${event.playerName} ${event.highlights.map((highlight) => highlight.text).join(", ")}.`;
}

/**
 * Opens a round's chapter of the feed, once per round: the server sends many
 * states per round, and only the first one of a new round adds the divider.
 */
export function startRound(
    events: readonly LoggedEvent[],
    round: number,
    roundMinimum: number
): readonly LoggedEvent[] {
    const text = `Round ${round} · ${roundMinimum} to open`;
    const current = events.findLast((event) => event.kind === "round");

    if (current?.text === text) {
        return events;
    }

    return appendEvent(events, { kind: "round", text });
}
